import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getCampaign,
  getCampaignContacts,
  startCampaign,
  pauseCampaign,
} from '@/lib/campaigns';
import type { Campaign, Contact } from '@/types/campaigns';
import {
  ArrowLeft,
  Play,
  Pause,
  RefreshCw,
  Phone,
  PhoneOff,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  MessageSquare,
} from 'lucide-react';

function getStatusBadgeVariant(status: string) {
  switch (status) {
    case 'running':
      return 'success';
    case 'completed':
      return 'secondary';
    case 'paused':
      return 'warning';
    default:
      return 'outline';
  }
}

function getContactStatusIcon(status: string, outcome?: string) {
  if (status === 'completed') {
    if (outcome === 'answered')
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (outcome === 'no_answer' || outcome === 'busy')
      return <PhoneOff className="h-4 w-4 text-yellow-600" />;
    return <XCircle className="h-4 w-4 text-red-600" />;
  }
  if (status === 'calling')
    return <Phone className="h-4 w-4 animate-pulse text-blue-600" />;
  return <Clock className="h-4 w-4 text-muted-foreground" />;
}

function formatDate(dateString?: string) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Survey response labels and colors
const SURVEY_RESPONSE_CONFIG: Record<string, { label: string; color: string }> =
  {
    yes: { label: 'Yes', color: 'text-green-600' },
    no: { label: 'No', color: 'text-red-600' },
    maybe: { label: 'Maybe', color: 'text-yellow-600' },
    callback: { label: 'Callback', color: 'text-blue-600' },
    wrong_number: { label: 'Wrong Number', color: 'text-orange-600' },
    refused: { label: 'Refused', color: 'text-red-400' },
    unclear: { label: 'Unclear', color: 'text-muted-foreground' },
  };

function aggregateSurveyResponses(contacts: Contact[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const contact of contacts) {
    if (contact.survey_response) {
      counts[contact.survey_response] =
        (counts[contact.survey_response] || 0) + 1;
    }
  }
  return counts;
}

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');

      const [campaignData, contactsData] = await Promise.all([
        getCampaign(id),
        getCampaignContacts(
          id,
          statusFilter === 'all' ? undefined : statusFilter,
        ),
      ]);

      setCampaign(campaignData);
      setContacts(contactsData.contacts);
      setTotalContacts(contactsData.total);
    } catch (err) {
      setError('Failed to load campaign');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh when campaign is running
  useEffect(() => {
    if (campaign?.status !== 'running') return;

    const interval = setInterval(loadData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [campaign?.status, loadData]);

  const handleStart = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      await startCampaign(id);
      await loadData();
    } catch (err) {
      console.error('Failed to start campaign:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    if (!id) return;
    try {
      setActionLoading(true);
      await pauseCampaign(id);
      await loadData();
    } catch (err) {
      console.error('Failed to pause campaign:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !campaign) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && !campaign) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/campaigns">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <Card className="border-destructive">
          <CardContent className="py-4">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!campaign) return null;

  const progress =
    campaign.total > 0
      ? Math.round((campaign.completed / campaign.total) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/campaigns">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{campaign.name}</h1>
              <Badge variant={getStatusBadgeVariant(campaign.status)}>
                {campaign.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {campaign.type} · Created {formatDate(campaign.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          {campaign.status === 'pending' || campaign.status === 'paused' ? (
            <Button onClick={handleStart} disabled={actionLoading}>
              <Play className="mr-2 h-4 w-4" />
              Start Campaign
            </Button>
          ) : campaign.status === 'running' ? (
            <Button
              variant="outline"
              onClick={handlePause}
              disabled={actionLoading}
            >
              <Pause className="mr-2 h-4 w-4" />
              Pause Campaign
            </Button>
          ) : null}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Contacts
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{campaign.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Answered</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {campaign.answered}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">No Answer</CardTitle>
            <PhoneOff className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {campaign.no_answer}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {campaign.failed}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Progress value={progress} className="flex-1" />
            <span className="text-sm font-medium">
              {campaign.completed} / {campaign.total} ({progress}%)
            </span>
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            Time window: {campaign.start_hour.toString().padStart(2, '0')}:00 -{' '}
            {campaign.end_hour.toString().padStart(2, '0')}:00 (
            {campaign.timezone})
          </div>
        </CardContent>
      </Card>

      {/* Survey Breakdown - only show if campaign has call_reason and survey enabled */}
      {campaign.call_reason && campaign.enable_survey && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle>Survey Results</CardTitle>
                <CardDescription className="mt-1">
                  "{campaign.call_reason}"
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const surveyData = aggregateSurveyResponses(contacts);
              const totalResponses = Object.values(surveyData).reduce(
                (a, b) => a + b,
                0,
              );

              if (totalResponses === 0) {
                return (
                  <p className="text-sm text-muted-foreground">
                    No survey responses yet
                  </p>
                );
              }

              return (
                <div className="space-y-3">
                  {Object.entries(surveyData)
                    .sort(([, a], [, b]) => b - a)
                    .map(([response, count]) => {
                      const config = SURVEY_RESPONSE_CONFIG[response] || {
                        label: response,
                        color: 'text-muted-foreground',
                      };
                      const percentage = Math.round(
                        (count / totalResponses) * 100,
                      );

                      return (
                        <div
                          key={response}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${config.color}`}>
                              {config.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {count}
                            </span>
                            <span className="w-12 text-right text-sm font-medium">
                              {percentage}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  <div className="border-t pt-2 text-sm text-muted-foreground">
                    Total responses: {totalResponses}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Contacts List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Contacts</CardTitle>
              <CardDescription>
                {totalContacts} contact{totalContacts !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="calling">Calling</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="no_answer">No Answer</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Attempted</TableHead>
                <TableHead>Completed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No contacts found
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getContactStatusIcon(contact.status, contact.outcome)}
                        <span className="capitalize">{contact.status}</span>
                      </div>
                    </TableCell>
                    <TableCell>{contact.name || '-'}</TableCell>
                    <TableCell className="font-mono">{contact.phone}</TableCell>
                    <TableCell>
                      {contact.outcome ? (
                        <Badge
                          variant={
                            contact.outcome === 'answered'
                              ? 'success'
                              : contact.outcome === 'no_answer' ||
                                  contact.outcome === 'busy'
                                ? 'warning'
                                : 'destructive'
                          }
                        >
                          {contact.outcome}
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(contact.attempted_at)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(contact.completed_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
